import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Audience, steps } from '../data/steps';
import { Reveal } from './Reveal';
const labels: Record<Audience, string> = {
  companies: 'Companies',
  taskers: 'Taskers'
};
const ease = [0.23, 1, 0.32, 1] as const;
export function HowItWorks() {
  const reduce = useReducedMotion();
  const [audience, setAudience] = useState<Audience>('companies');
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const list = steps[audience];
  useEffect(() => setActive(0), [audience]);
  useEffect(() => {
    if (reduce || paused) return;
    const id = window.setInterval(() => setActive(a => (a + 1) % list.length), 2200);
    return () => window.clearInterval(id);
  }, [reduce, paused, list.length, audience]);
  return <section id="how" className="scroll-mt-20 bg-ink text-canvas" data-id="element-84" data-mp="14h1gqydjkh6a">
      <div className="mx-auto max-w-6xl px-6 py-28" data-id="element-85" data-mp="19anvqx76gx59">
        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between" data-id="element-86" data-mp="2rlo0zlrmz323">
          <Reveal data-id="element-87" data-mp="2mml3qnhwe14o">
            <p className="font-mono text-sm text-accent" data-id="element-88" data-mp="2mn39caqg9rp9">brief → human → proof</p>
            <h2 className="mt-3 max-w-xl font-display text-4xl font-semibold tracking-tight md:text-5xl" data-id="element-89" data-mp="31bhamh87x83p">Brief in. Proof out.</h2>
          </Reveal>
          <div role="tablist" aria-label="Audience" className="inline-flex w-fit rounded-full border border-canvas/15 p-1" data-id="element-90" data-mp="2wjz83oqjqzgm">
            {(Object.keys(labels) as Audience[]).map(a => <button key={a} role="tab" aria-selected={audience === a} onClick={() => setAudience(a)} className={`relative whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-150 ${audience === a ? 'text-ink' : 'text-canvas/70 hover:text-canvas'}`} data-id="element-91" data-mp="369isiytwr1dl">
                {audience === a && <motion.span layoutId="how-audience" className="absolute inset-0 rounded-full bg-canvas" transition={{
              duration: 0.22,
              ease
            }} data-id="element-92" data-mp="1kzbzwwrofwqc" />}
                <span className="relative" data-id="element-93" data-mp="3pxs5bj9dtscz">{labels[a]}</span>
              </button>)}
          </div>
        </div>

        <div className="relative mt-16" data-id="element-94" data-mp="3tzdmhgsy8ssz">
          <div className="absolute left-5 top-5 hidden h-px w-2/3 bg-canvas/15 md:block" aria-hidden="true" data-id="element-95" data-mp="2tuf2pxz7fpa">
            <motion.div className="h-full bg-accent" animate={{
            width: `${active * 50}%`
          }} transition={{
            duration: 0.3,
            ease
          }} data-id="element-96" data-mp="7t70d6978epr" />
            <motion.span className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent shadow-[0_0_0_4px_rgba(34,211,128,0.2)]" animate={{
            left: `${active * 50}%`
          }} transition={{
            duration: 0.3,
            ease
          }} data-id="element-97" data-mp="cr07c1ibtcr3" />
          </div>

          <ol className="grid gap-10 md:grid-cols-3 md:gap-0" data-id="element-98" data-mp="qkrex4chymk6">
            {list.map((step, i) => {
            const state = i === active ? 'active' : i < active ? 'done' : 'todo';
            return <li key={`${audience}-${step.title}`} className="md:pr-10" data-id="element-99" data-mp="15o17hnxfvqn3" data-mpi={i}>
                  <button type="button" onMouseEnter={() => {
                setActive(i);
                setPaused(true);
              }} onMouseLeave={() => setPaused(false)} onFocus={() => {
                setActive(i);
                setPaused(true);
              }} onBlur={() => setPaused(false)} className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-4 focus-visible:ring-offset-ink" data-id="element-100" data-mp="10mbuw3ukj4ww">
                    <span className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border font-mono text-sm font-medium transition-colors duration-200 ${state === 'active' ? 'border-accent bg-accent text-accent-ink' : state === 'done' ? 'border-accent bg-ink text-accent' : 'border-canvas/25 bg-ink text-canvas/70'}`} data-id="element-101" data-mp="1ajglcfvg5mji">
                      {i + 1}
                    </span>
                    <span className="mt-6 block font-mono text-xs text-canvas/50" data-id="element-102" data-mp="1hxpa9dpppso6">
                      <span className={`transition-colors duration-200 ${state === 'active' ? 'text-accent' : ''}`} data-id="element-103" data-mp="25lqmawm3nr8c">● </span>
                      {step.event}
                    </span>
                    <span className={`mt-2 block text-xl font-semibold transition-colors duration-200 ${state === 'active' ? 'text-canvas' : 'text-canvas/85'}`} data-id="element-104" data-mp="1ws3xxrk81q96">
                      {step.title}
                    </span>
                    <span className="mt-2 block leading-relaxed text-canvas/65" data-id="element-105" data-mp="2ohylpjsfl7vq">{step.description}</span>
                  </button>
                </li>;
          })}
          </ol>
        </div>
      </div>
    </section>;
}